import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { CheckCircle2, XCircle, Clock, Circle, ChevronDown, ChevronUp, X } from 'lucide-react';
import { STAGE_LABELS, HUMAN_REVIEW_STAGES, stageOrderFor, STATUS_BADGE } from './constants';
import VideoPlayer from './VideoPlayer';

const API_URL = import.meta.env.VITE_API_URL;

function ScoreBar({ score }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${score >= 4 ? 'bg-emerald-500' : score >= 3 ? 'bg-amber-400' : 'bg-red-400'}`}
          style={{ width: `${(score / 5) * 100}%` }}
        />
      </div>
      <span className="text-xs font-medium text-gray-600 w-6">{score}/5</span>
    </div>
  );
}

function StageDetail({ stageResult }) {
  if (!stageResult) return <p className="text-xs text-gray-400">No result recorded yet.</p>;

  return (
    <div className="space-y-2">
      {stageResult.rawScore != null && (
        <p className="text-xs text-gray-600">
          Score: <strong>{stageResult.rawScore}%</strong>
          {stageResult.passThreshold != null && <span className="text-gray-400"> (pass mark: {stageResult.passThreshold}%)</span>}
        </p>
      )}
      {stageResult.competencyScores?.length > 0 && (
        <div className="space-y-2">
          {stageResult.competencyScores.map((c) => (
            <div key={c.competencyKey}>
              <div className="flex items-center justify-between text-xs text-gray-600 mb-0.5">
                <span className="font-medium">{c.competencyKey.replace(/_/g, ' ')}</span>
                {c.source === 'ai' && <span className="text-[10px] bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded">AI</span>}
              </div>
              <ScoreBar score={c.score} />
              {c.evidence && <p className="text-xs italic text-gray-400 mt-0.5">&quot;{c.evidence}&quot;</p>}
            </div>
          ))}
        </div>
      )}
      {stageResult.answerData?.videos?.map((v, i) => (
        <div key={i} className="mt-2 border-t border-gray-100 pt-2">
          <p className="text-xs font-medium text-gray-600 mb-1">{v.prompt}</p>
          {v.videoUrl ? (
            <VideoPlayer videoUrl={v.videoUrl} />
          ) : (
            <p className="text-xs text-gray-400 italic mb-1">
              Video deleted per retention policy{v.deletedAt ? ` (on ${new Date(v.deletedAt).toLocaleDateString()})` : ''} — transcript kept below.
            </p>
          )}
          {v.transcript && <p className="text-xs text-gray-500 mt-1 bg-gray-100 rounded p-2">&quot;{v.transcript}&quot;</p>}
        </div>
      ))}
      {stageResult.reviewNotes && (
        <p className="text-xs text-gray-500 mt-1">Notes: {stageResult.reviewNotes}</p>
      )}
    </div>
  );
}

function CandidateProfile({ applicationId, rubrics, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStage, setExpandedStage] = useState(null);
  const [scores, setScores] = useState({});
  const [reviewNotes, setReviewNotes] = useState('');
  const [knockout, setKnockout] = useState(false);
  const [knockoutReason, setKnockoutReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/recruitment/applications/${applicationId}`)
      .then((res) => {
        setDetail(res.data);
        const currentResult = res.data.stageResults.find((s) => s.stage === res.data.application.currentStage);
        const seed = {};
        currentResult?.competencyScores?.forEach((c) => { seed[c.competencyKey] = c.score; });
        setScores(seed);
        setReviewNotes(currentResult?.reviewNotes || '');
        setExpandedStage(res.data.application.currentStage);
      })
      .catch(() => toast.error('Failed to load candidate'))
      .finally(() => setLoading(false));
  }, [applicationId]);

  if (loading || !detail) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 text-sm text-gray-500">Loading...</div>
      </div>
    );
  }

  const { application, stageResults } = detail;
  const rubric = rubrics.find((r) => r.roleFamily === application.roleFamily);
  const currentStage = application.currentStage;
  const order = stageOrderFor(application.craft);
  const currentIdx = order.indexOf(currentStage);
  const currentStageResult = stageResults.find((s) => s.stage === currentStage);
  const needsReview = HUMAN_REVIEW_STAGES.includes(currentStage) && application.status === 'in_progress';
  const readyForDecision = application.status === 'in_progress'
    && currentStage === 'physical_meeting'
    && currentStageResult?.outcome === 'pass'
    && currentStageResult?.reviewStatus === 'reviewed';

  const stageStatus = (stage, idx) => {
    const result = stageResults.find((s) => s.stage === stage);
    if (result?.outcome === 'pass') return 'pass';
    if (result?.outcome === 'fail') return 'fail';
    if (result?.reviewStatus === 'needs_review') return 'needs_review';
    if (idx === currentIdx) return 'current';
    if (idx > currentIdx) return 'upcoming';
    return 'current';
  };

  const submitReview = async (outcomeOverride) => {
    setSubmitting(true);
    try {
      const competencyScores = rubric
        ? rubric.competencies.map((c) => ({
            competencyKey: c.key,
            score: Number(scores[c.key]) || 3,
            source: currentStage === 'video' ? 'ai' : 'human',
          }))
        : [];
      await axios.put(`${API_URL}/api/recruitment/applications/${application.id}/review`, {
        stage: currentStage, competencyScores, outcomeOverride,
        knockoutTriggered: knockout, knockoutReason, reviewedBy: 'admin', reviewNotes,
      });
      toast.success('Review saved');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save review');
    } finally {
      setSubmitting(false);
    }
  };

  const submitDecision = async (decision) => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/recruitment/applications/${application.id}/decision`, { decision, notes: reviewNotes });
      toast.success(decision === 'offer' ? 'Offer sent' : 'Candidate rejected');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record decision');
    } finally {
      setSubmitting(false);
    }
  };

  const iconFor = (status) => {
    if (status === 'pass') return <CheckCircle2 size={20} className="text-emerald-500" />;
    if (status === 'fail') return <XCircle size={20} className="text-red-500" />;
    if (status === 'needs_review') return <Clock size={20} className="text-amber-500" />;
    if (status === 'current') return <Circle size={20} className="text-blue-500 fill-blue-100" />;
    return <Circle size={20} className="text-gray-300" />;
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex justify-between items-start p-6 pb-4 border-b border-gray-100 z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-800">{application.candidate.name}</h2>
            <p className="text-sm text-gray-500">{application.roleLabel} · {application.candidate.email} · {application.candidate.phone}</p>
            <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[application.status] || 'bg-gray-100 text-gray-700'}`}>
              {application.status.replace('_', ' ')}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
        </div>

        <div className="p-6 pt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Pipeline</h3>
          <div className="space-y-1 mb-6">
            {order.map((stage, idx) => {
              const status = stageStatus(stage, idx);
              const result = stageResults.find((s) => s.stage === stage);
              const isExpanded = expandedStage === stage;
              const isExpandable = status !== 'upcoming';
              return (
                <div key={stage} className="border border-gray-100 rounded-lg overflow-hidden">
                  <button
                    onClick={() => isExpandable && setExpandedStage(isExpanded ? null : stage)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-left ${isExpandable ? 'hover:bg-gray-50' : 'opacity-50 cursor-default'}`}
                  >
                    <div className="flex items-center gap-3">
                      {iconFor(status)}
                      <span className="text-sm font-medium text-gray-700">{STAGE_LABELS[stage]}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {status === 'needs_review' && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">NEEDS REVIEW</span>}
                      {isExpandable && (isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />)}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 pt-1 bg-gray-50 border-t border-gray-100">
                      <StageDetail stageResult={result} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {needsReview && rubric && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Review: {STAGE_LABELS[currentStage]}
                {currentStage === 'video' && <span className="ml-2 text-xs text-gray-400 font-normal">(AI-scored — confirm or edit)</span>}
              </h3>
              <div className="space-y-3">
                {rubric.competencies.map((c) => (
                  <div key={c.key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{c.label}</label>
                    <input
                      type="range" min={1} max={5}
                      value={scores[c.key] || 3}
                      onChange={(e) => setScores((s) => ({ ...s, [c.key]: Number(e.target.value) }))}
                      className="w-full accent-emerald-600"
                    />
                    <span className="text-xs text-gray-500">{scores[c.key] || 3} / 5 — {c.anchors[scores[c.key] || 3]}</span>
                  </div>
                ))}
              </div>

              <div className="mt-3">
                <label className="flex items-center gap-2 text-sm text-red-600">
                  <input type="checkbox" checked={knockout} onChange={(e) => setKnockout(e.target.checked)} />
                  Knockout (integrity/safety/reliability red flag — forces rejection)
                </label>
                {knockout && (
                  <input
                    value={knockoutReason} onChange={(e) => setKnockoutReason(e.target.value)}
                    placeholder="Reason" className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                )}
              </div>

              <textarea
                value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Notes (optional)" rows={2}
                className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />

              <div className="flex gap-2 mt-4">
                <button disabled={submitting} onClick={() => submitReview('pass')} className="flex-1 bg-emerald-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">Confirm Pass</button>
                <button disabled={submitting} onClick={() => submitReview('fail')} className="flex-1 bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">Reject</button>
              </div>
            </div>
          )}

          {readyForDecision && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Final Decision</h3>
              <div className="flex gap-2">
                <button disabled={submitting} onClick={() => submitDecision('offer')} className="flex-1 bg-emerald-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">Extend Offer</button>
                <button disabled={submitting} onClick={() => submitDecision('reject')} className="flex-1 bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">Reject</button>
              </div>
            </div>
          )}

          {!needsReview && !readyForDecision && application.status === 'in_progress' && (
            <p className="text-sm text-gray-400 border-t border-gray-200 pt-4">
              Waiting on the candidate at the {STAGE_LABELS[currentStage] || currentStage} stage.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default CandidateProfile;
